import React from "react";

/**
 * A simple markdown editor: textarea + a small toolbar that wraps selection in markdown syntax.
 * No external editor dependency. Renders a hint of supported syntax.
 */
const TOKENS = {
  bold:        { wrap: "**", placeholder: "bold text" },
  italic:      { wrap: "*",  placeholder: "italic text" },
  quote:       { prefix: "> ", placeholder: "a quote" },
  bullet:      { prefix: "- ", placeholder: "list item" },
  code:        { wrap: "`",  placeholder: "code" },
};

export default function MarkdownEditor({ value, onChange, placeholder, minLength = 1, maxLength = 5000, testIdPrefix = "md" }) {
  const ref = React.useRef(null);

  const apply = (token) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    let inserted, caretStart, caretEnd;
    if (token.wrap) {
      const text = sel || token.placeholder;
      inserted = `${token.wrap}${text}${token.wrap}`;
      caretStart = before.length + token.wrap.length;
      caretEnd = caretStart + text.length;
    } else if (token.prefix) {
      // line-based prefix
      const lineStart = before.lastIndexOf("\n") + 1;
      const newBefore = value.slice(0, lineStart);
      const lineContent = value.slice(lineStart, end);
      const text = sel ? lineContent : token.placeholder;
      inserted = `${token.prefix}${text}`;
      const newValue = `${newBefore}${inserted}${after}`;
      onChange(newValue);
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = newBefore.length + token.prefix.length;
        ta.selectionEnd = newBefore.length + token.prefix.length + text.length;
      });
      return;
    }
    const newValue = `${before}${inserted}${after}`;
    onChange(newValue);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = caretStart;
      ta.selectionEnd = caretEnd;
    });
  };

  return (
    <div data-testid={`${testIdPrefix}-editor`}>
      <div className="flex flex-wrap items-center gap-1 mb-2 text-xs text-[#5C6B5F]">
        <ToolBtn onClick={() => apply(TOKENS.bold)} testId={`${testIdPrefix}-bold`}>
          <span className="font-bold">B</span>
        </ToolBtn>
        <ToolBtn onClick={() => apply(TOKENS.italic)} testId={`${testIdPrefix}-italic`}>
          <em>I</em>
        </ToolBtn>
        <ToolBtn onClick={() => apply(TOKENS.quote)} testId={`${testIdPrefix}-quote`}>
          “”
        </ToolBtn>
        <ToolBtn onClick={() => apply(TOKENS.bullet)} testId={`${testIdPrefix}-bullet`}>
          • list
        </ToolBtn>
        <ToolBtn onClick={() => apply(TOKENS.code)} testId={`${testIdPrefix}-code`}>
          {"</>"}
        </ToolBtn>
        <span className="ml-auto text-[#8A948C]">
          Markdown supported · {(value || "").length}/{maxLength}
        </span>
      </div>
      <textarea
        ref={ref}
        className="input-organic min-h-[140px] font-sans-ui"
        placeholder={placeholder}
        value={value}
        minLength={minLength}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testIdPrefix}-textarea`}
      />
    </div>
  );
}

function ToolBtn({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1 rounded-md border border-[#EAE5D9] hover:border-[#D96C4A] hover:text-[#D96C4A] transition-colors text-xs"
      data-testid={testId}
    >
      {children}
    </button>
  );
}
